import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import StatCard from "./components/StatCard";
import DashboardCard from "./components/DashboardCard";
import SectionEmpty from "./components/SectionEmpty";
import DonutChart, { type DonutSlice } from "./components/DonutChart";
import ProgressStat from "./components/ProgressStat";
import { cn } from "@/utils/cn";
import { useSession } from "@/session/SessionProvider";
import { fullName } from "@/session/types";
import { useDgStats } from "@/hooks/useDgStats";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useLocataires } from "@/pages/proprietaire/locataires/LocatairesProvider";
import { useLoyers } from "@/pages/proprietaire/loyers/LoyersProvider";
import { monthLabel } from "@/pages/proprietaire/loyers/periods";
import { LOYER_STATUT_LABELS, type LoyerStatut } from "@/pages/proprietaire/loyers/types";
import { locataireNomComplet } from "@/pages/proprietaire/locataires/types";
import { formatDate, formatFcfa } from "@/utils/format";

const REPARTITION_TOKENS = ["primary-500", "accent-500", "secondary-500", "foreground-700", "primary-700", "accent-700"];

const STATUT_TOKENS: Record<LoyerStatut, string> = {
  paye: "primary-500",
  "a-venir": "secondary-500",
  "en-retard": "accent-500",
  impaye: "foreground-700",
};

type RepartitionMode = "statut" | "immeuble";

/**
 * Tableau de bord du Directeur Propriétaire.
 *
 * Les indicateurs proviennent réellement de Supabase :
 * - compteurs métier via la RPC sécurisée `stats_dashboard_dg()` ;
 * - immeubles / biens / locataires via les tables réelles ;
 * - loyers / impayés calculés depuis les contrats et paiements réels.
 * Aucune donnée fictive : un patrimoine vide affiche des états vides.
 */
export default function OwnerDashboard() {
  const { session } = useSession();
  const firstName = session ? fullName(session).split(" ")[0] : "";
  const { stats, error, reload } = useDgStats();
  const { immeubles } = useImmeubles();
  const { locataires, getLocataire } = useLocataires();
  const { loyers, impayes } = useLoyers();

  const [repartitionMode, setRepartitionMode] = useState<RepartitionMode>("statut");

  const tauxOccupation = stats.biens > 0 ? (stats.biens_occupes / stats.biens) * 100 : 0;
  const tauxDisponibilite = stats.biens > 0 ? (stats.biens_disponibles / stats.biens) * 100 : 0;
  const tauxContrats = stats.biens > 0 ? (stats.contrats_actifs / stats.biens) * 100 : 0;

  const statutSlices = useMemo<DonutSlice[]>(() => {
    const statuts: LoyerStatut[] = ["paye", "a-venir", "en-retard", "impaye"];
    return statuts.map((statut) => ({
      key: statut,
      label: LOYER_STATUT_LABELS[statut],
      token: STATUT_TOKENS[statut],
      value: loyers.filter((loyer) => loyer.statut === statut).reduce((sum, loyer) => sum + loyer.montant, 0),
    }));
  }, [loyers]);

  const immeubleSlices = useMemo<DonutSlice[]>(
    () =>
      immeubles
        .map((immeuble, index) => ({
          key: immeuble.id,
          label: immeuble.nom,
          token: REPARTITION_TOKENS[index % REPARTITION_TOKENS.length],
          value: loyers.filter((loyer) => loyer.immeubleId === immeuble.id).reduce((sum, loyer) => sum + loyer.montant, 0),
        }))
        .filter((slice) => slice.value > 0),
    [immeubles, loyers]
  );

  const activeSlices = repartitionMode === "statut" ? statutSlices : immeubleSlices;
  const repartitionTotal = activeSlices.reduce((sum, slice) => sum + slice.value, 0);

  const loyersAVenir = loyers.filter((loyer) => loyer.statut === "a-venir").slice(0, 5);
  const topImpayes = impayes.slice(0, 5);

  const locataireLabel = (id: string) => {
    const locataire = getLocataire(id);
    return locataire ? locataireNomComplet(locataire) : "Locataire non renseigné";
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
            Bonjour{firstName ? ` ${firstName}` : ""}
          </h2>
          <p className="mt-1 text-sm text-foreground-600">
            Voici un aperçu de votre patrimoine en FCFA. Commencez par créer votre premier immeuble.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Link
            to="/proprietaire/immeubles"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Ajouter un immeuble
          </Link>
          <Link
            to="/proprietaire/locataires"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-user-add-line text-base" aria-hidden="true" />
            Ajouter un locataire
          </Link>
        </div>
      </div>

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-background-100 px-3.5 py-3 text-xs text-foreground-700">
          <span className="flex items-center gap-1.5">
            <i className="ri-error-warning-line text-base text-accent-600" aria-hidden="true" />
            Impossible de charger les statistiques. {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-1 whitespace-nowrap font-label font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
          >
            <i className="ri-refresh-line text-sm" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-building-2-line" label="Immeubles" value={String(stats.immeubles)} tone="primary" />
        <StatCard icon="ri-home-4-line" label="Biens" value={String(stats.biens)} tone="secondary" />
        <StatCard icon="ri-home-smile-2-line" label="Biens occupés" value={String(stats.biens_occupes)} tone="accent" />
        <StatCard icon="ri-home-8-line" label="Biens disponibles" value={String(stats.biens_disponibles)} tone="neutral" />
        <StatCard icon="ri-user-3-line" label="Locataires" value={String(stats.locataires)} tone="primary" />
        <StatCard icon="ri-file-text-line" label="Contrats actifs" value={String(stats.contrats_actifs)} tone="secondary" />
        <StatCard
          icon="ri-wallet-3-line"
          label="Loyers à recouvrer"
          value={formatFcfa(stats.loyers_a_payer)}
          tone="neutral"
        />
        <StatCard
          icon="ri-alert-line"
          label="Loyers en retard"
          value={formatFcfa(stats.loyers_en_retard)}
          tone="accent"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <DashboardCard className="lg:col-span-2" title="Répartition des loyers" icon="ri-pie-chart-2-line">
          <div className="flex flex-col gap-5">
            <div className="inline-flex w-full rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setRepartitionMode("statut")}
                className={cn(
                  "flex-1 rounded-full px-1 py-1.5 text-xs font-label transition-colors cursor-pointer",
                  repartitionMode === "statut"
                    ? "bg-background-50 font-medium text-foreground-950 border border-background-200"
                    : "text-foreground-600 hover:text-foreground-900"
                )}
              >
                Par statut
              </button>
              <button
                type="button"
                onClick={() => setRepartitionMode("immeuble")}
                className={cn(
                  "flex-1 rounded-full px-1 py-1.5 text-xs font-label transition-colors cursor-pointer",
                  repartitionMode === "immeuble"
                    ? "bg-background-50 font-medium text-foreground-950 border border-background-200"
                    : "text-foreground-600 hover:text-foreground-900"
                )}
              >
                Par immeuble
              </button>
            </div>

            <DonutChart
              slices={activeSlices}
              centerValue={formatFcfa(repartitionTotal, { compact: true })}
              centerLabel="Total loyers"
              formatter={(value) => formatFcfa(value, { compact: true })}
              emptyLabel={
                repartitionMode === "statut"
                  ? "Aucun loyer à répartir pour le moment."
                  : "Aucun loyer rattaché à un immeuble pour le moment."
              }
            />
          </div>
        </DashboardCard>

        <DashboardCard className="lg:col-span-3" title="Indicateurs clés" icon="ri-donut-chart-line">
          <div className="flex flex-col gap-5">
            <ProgressStat
              icon="ri-home-smile-2-line"
              label="Taux d'occupation"
              detail={`${stats.biens_occupes} bien(s) occupé(s) sur ${stats.biens}`}
              value={tauxOccupation}
              tone="primary"
            />
            <ProgressStat
              icon="ri-home-8-line"
              label="Taux de disponibilité"
              detail={`${stats.biens_disponibles} bien(s) disponible(s) sur ${stats.biens}`}
              value={tauxDisponibilite}
              tone="accent"
            />
            <ProgressStat
              icon="ri-file-text-line"
              label="Contrats actifs"
              detail={`${stats.contrats_actifs} contrat(s) actif(s)`}
              value={tauxContrats}
              tone="secondary"
            />
          </div>
        </DashboardCard>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <DashboardCard
          className="lg:col-span-2"
          title="Loyers à venir"
          icon="ri-calendar-schedule-line"
          actionLabel="Voir les loyers"
          actionTo="/proprietaire/loyers"
        >
          {loyersAVenir.length === 0 ? (
            <SectionEmpty
              icon="ri-calendar-event-line"
              title="Aucun loyer à venir"
              description="Les prochains loyers apparaîtront ici dès que vous aurez créé vos contrats."
              actionLabel="Créer un contrat"
              actionTo="/proprietaire/contrats"
            />
          ) : (
            <ul className="flex flex-col divide-y divide-background-200">
              {loyersAVenir.map((loyer) => (
                <li key={loyer.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-label font-medium text-foreground-950">
                      {monthLabel(loyer.periode)} · {locataireLabel(loyer.locataireId)}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">
                      Échéance le {formatDate(loyer.echeance)}
                    </p>
                  </div>
                  <span className="shrink-0 font-heading text-sm font-semibold text-foreground-950">
                    {formatFcfa(loyer.montant)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DashboardCard>

        <DashboardCard title="Impayés" icon="ri-alert-line" actionLabel="Voir les impayés" actionTo="/proprietaire/impayes">
          {topImpayes.length === 0 ? (
            <SectionEmpty
              icon="ri-checkbox-circle-line"
              title="Aucun impayé"
              description="Aucun retard de paiement enregistré. Tout est à jour."
            />
          ) : (
            <ul className="flex flex-col divide-y divide-background-200">
              {topImpayes.map((loyer) => (
                <li key={loyer.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-label font-medium text-foreground-950">
                      {locataireLabel(loyer.locataireId)}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">
                      {monthLabel(loyer.periode)} · {loyer.joursRetard} j de retard
                    </p>
                  </div>
                  <span className="shrink-0 font-heading text-sm font-semibold text-primary-700">
                    {formatFcfa(loyer.reste)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DashboardCard>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <DashboardCard className="lg:col-span-2" title="Synthèse financière" icon="ri-hand-coin-line">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-md border border-background-200 bg-background-100 p-4">
              <p className="text-xs text-foreground-500">Loyers à recouvrer</p>
              <p className="mt-1 font-heading text-xl font-semibold text-foreground-950">
                {formatFcfa(stats.loyers_a_payer)}
              </p>
            </div>
            <div className="rounded-md border border-background-200 p-4">
              <p className="text-xs text-foreground-500">Loyers en retard</p>
              <p className="mt-1 font-heading text-xl font-semibold text-accent-700">
                {formatFcfa(stats.loyers_en_retard)}
              </p>
            </div>
          </div>
          <p className="mt-4 text-xs text-foreground-400">
            Montants calculés en temps réel par Supabase sur votre périmètre (immeubles, biens, locataires,
            contrats et paiements réels).
          </p>
        </DashboardCard>

        <DashboardCard
          title="Locataires"
          icon="ri-user-3-line"
          actionLabel="Voir les locataires"
          actionTo="/proprietaire/locataires"
        >
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-background-200 bg-background-100 p-4">
              <p className="text-xs text-foreground-500">Locataires enregistrés</p>
              <p className="mt-1 font-heading text-xl font-semibold text-foreground-950">
                {locataires.length}
              </p>
            </div>
            <p className="text-xs text-foreground-400">
              {locataires.length} locataire{locataires.length > 1 ? "s" : ""} rattaché
              {locataires.length > 1 ? "s" : ""} à votre patrimoine.
            </p>
          </div>
        </DashboardCard>
      </div>
    </div>
  );
}